import produce from 'immer';
import {
  types,
  Config,
  MembershipProductConfig,
  TicketingState,
  TicketingActionTypes,
  SetGenresConfig,
  SetEventsConfig,
  GetEventsAsyncSuccess,
  GetEventsAsyncError,
  GetEventsAsyncPending,
  GetEventDetailsAsyncError,
  GetEventDetailsAsyncPending,
  GetEventDetailsAsyncSuccess,
  GetMembershipProductAsyncError,
  GetMembershipProductAsyncPending,
  GetMembershipProductAsyncSuccess,
  ResetEventDataAsyncError,
  ResetEventDataAsyncPending,
  SetTiersConfig,
  SetMembershipProductConfig,
  CreateSessionAsyncError,
  CreateSessionAsyncPending,
  CreateSessionAsyncSuccess,
  ApplyPriceTypePromoCodeAsyncError,
  ApplyPriceTypePromoCodeAsyncPending,
  ApplyPriceTypePromoCodeAsyncRequest,
  ApplyPriceTypePromoCodeAsyncSuccess,
  ValidateMembershipAsyncError,
  ValidateMembershipAsyncPending,
  ValidateMembershipAsyncRequest,
  ValidateMembershipAsyncSuccess,
  SavePreferredDeliveryMethod,
  SaveDeliveryEmail,
  SaveDeliveryPhoneNumber,
  EmailTicketsAsyncRequest,
  EmailTicketsAsyncSuccess,
  EmailTicketsAsyncError,
  EmailTicketsAsyncPending,
  SetConfigPath,
  SetNavigationPath,
  SetTimerOverlay,
  SetDeliveryStatus,
  TextTicketsAsyncRequest,
  TextTicketsAsyncPending,
  TextTicketsAsyncSuccess,
  TextTicketsAsyncError,
  UpdatePrintCountRequest,
  SetEventDetails,
  SetMembershipProduct,
  SetUpsell,
  SetSelectedPromoCode
} from './types';

// Actions
export const actions = {
  createSession: (): TicketingActionTypes => ({ type: types.CREATE_SESSION_ASYNC.REQUEST }),
  getEvents: (): TicketingActionTypes => ({ type: types.GET_EVENTS_ASYNC.REQUEST }),
  getEventDetails: (): TicketingActionTypes => ({ type: types.GET_EVENTDETAILS_ASYNC.REQUEST }),
  getMembershipProduct: (): TicketingActionTypes => ({ type: types.GET_MEMBERSHIP_PRODUCT_ASYNC.REQUEST }),
  setEventDetails: (payload: SetEventDetails['payload']): TicketingActionTypes => ({
    type: types.SET_EVENT_DETAILS,
    payload
  }),
  setMembershipProduct: (payload: SetMembershipProduct['payload']): TicketingActionTypes => ({
    type: types.SET_MEMBERSHIP_PRODUCT,
    payload
  }),
  resetSession: (): TicketingActionTypes => ({ type: types.RESET_SESSION }),
  applyPriceTypePromoCode: (payload: ApplyPriceTypePromoCodeAsyncRequest['payload']): TicketingActionTypes => ({
    type: types.APPLY_PRICE_TYPE_PROMO_CODE_ASYNC.REQUEST,
    payload
  }),
  resetPriceTypePromoCode: (): TicketingActionTypes => ({ type: types.RESET_PRICE_TYPE_PROMO_CODE }),
  resetAppliedPriceTypePromo: (): TicketingActionTypes => ({ type: types.RESET_APPLIED_PRICE_TYPE_PROMO }),
  setGenresConfig: (config: Config): TicketingActionTypes => ({ type: types.SET_GENRES_CONFIG, payload: config }),
  setEventsConfig: (config: Config): TicketingActionTypes => ({ type: types.SET_EVENTS_CONFIG, payload: config }),
  setTiersConfig: (config: Config): TicketingActionTypes => ({ type: types.SET_TIERS_CONFIG, payload: config }),
  setMembershipProductConfig: (config: MembershipProductConfig): TicketingActionTypes => ({
    type: types.SET_MEMBERSHIP_PRODUCT_CONFIG,
    payload: config
  }),
  resetTicketing: (): TicketingActionTypes => ({ type: types.RESET_TICKETING }),
  resetEvents: (): TicketingActionTypes => ({ type: types.RESET_EVENTS }),
  resetUpsellProducts: (): TicketingActionTypes => ({ type: types.RESET_UPSELL_PRODUCTS }),
  resetEventDetails: (): TicketingActionTypes => ({ type: types.RESET_EVENT_DETAILS }),
  resetUpsell: (): TicketingActionTypes => ({ type: types.RESET_UPSELL }),
  resetDelivery: (): TicketingActionTypes => ({ type: types.RESET_DELIVERY }),
  resetMembershipProduct: (): TicketingActionTypes => ({ type: types.RESET_MEMBERSHIP_PRODUCT }),
  resetEventData: (): TicketingActionTypes => ({ type: types.RESET_EVENT_DATA_ASYNC.REQUEST }),
  resetError: (): TicketingActionTypes => ({ type: types.RESET_ERROR }),
  resetSelectedPromoCode: (): TicketingActionTypes => ({ type: types.RESET_SELECTED_PROMO_CODE }),
  validateMembership: (payload: ValidateMembershipAsyncRequest['payload']): TicketingActionTypes => ({
    type: types.VALIDATE_MEMBERSHIP_ASYNC.REQUEST,
    payload
  }),
  resetMembership: (): TicketingActionTypes => ({ type: types.RESET_MEMBERSHIP_DETAILS }),
  savePreferredDeliveryMethod: (payload: SavePreferredDeliveryMethod['payload']): TicketingActionTypes => ({
    type: types.SAVE_PREFERRED_DELIVERY_METHOD,
    payload
  }),
  saveDeliveryEmail: (payload: SaveDeliveryEmail['payload']): TicketingActionTypes => ({
    type: types.SAVE_DELIVERY_EMAIL,
    payload
  }),
  saveDeliveryPhoneNumber: (payload: SaveDeliveryPhoneNumber['payload']): TicketingActionTypes => ({
    type: types.SAVE_DELIVERY_PHONE_NUMBER,
    payload
  }),
  emailTickets: (payload: EmailTicketsAsyncRequest['payload']): TicketingActionTypes => ({
    type: types.EMAIL_TICKETS_ASYNC.REQUEST,
    payload
  }),
  sendTextTickets: (payload: TextTicketsAsyncRequest['payload']): TicketingActionTypes => ({
    type: types.TEXT_TICKETS_ASYNC.REQUEST,
    payload
  }),
  updatePrintCount: (payload: UpdatePrintCountRequest['payload']): TicketingActionTypes => ({
    type: types.UPDATE_PRINT_COUNT_ASYNC.REQUEST,
    payload
  }),
  setConfigPath: (payload: string): TicketingActionTypes => ({ type: types.SET_CONFIG_PATH, payload }),
  setNavigationPath: (payload: string | null): TicketingActionTypes => ({ type: types.SET_NAVIGATION_PATH, payload }),
  setTimerOverlay: (payload: boolean): TicketingActionTypes => ({ type: types.SET_TIMER_OVERLAY, payload }),
  setDeliveryStatus: (payload: string): TicketingActionTypes => ({ type: types.SET_DELIVERY_STATUS, payload }),
  setUpsell: (payload: SetUpsell['payload']): TicketingActionTypes => ({ type: types.SET_UPSELL, payload }),
  setSelectedPromoCode: (payload: string | null): TicketingActionTypes => ({
    type: types.SET_SELECTED_PROMO_CODE,
    payload
  })
};

// Initial State
export const initialState: TicketingState = {
  events: [],
  eventDetails: null,
  membershipProduct: null,
  upsell: {
    initialProduct: {},
    promptProduct: {},
    singleUpsellProductOffered: false
  },
  upsellProducts: [],
  session: null,
  configPath: 'ticketing',
  navigationPath: null,
  timerOverlay: false,
  genresConfig: {
    page: 1,
    selected: ''
  },
  eventsConfig: {
    page: 1,
    selected: ''
  },
  tiersConfig: {
    page: 1,
    selected: {}
  },
  membershipProductConfig: {
    selected: ''
  },
  loading: false,
  fetchCompleted: {
    events: false,
    eventDetails: false,
    membershipProduct: false,
    upsellProducts: false
  },
  error: undefined,
  priceTypePromoCode: {
    loading: false,
    error: undefined,
    processed: false,
    selectedCode: undefined
  },
  membershipDetails: {
    isLoggedIn: false,
    membershipParams: undefined
  },
  delivery: {
    preferredMethod: null,
    email: '',
    phone: '',
    status: ''
  }
};

// Reducer
export const reducer = (state: TicketingState = initialState, action: TicketingActionTypes): TicketingState => {
  // eslint-disable-next-line consistent-return
  return produce(state, (draft) => {
    switch (action.type) {
      case types.GET_EVENTS_ASYNC.SUCCESS:
        if (draft.upsell.initialProduct.details) {
          draft.upsellProducts = (action as GetEventsAsyncSuccess).payload;
          draft.fetchCompleted.upsellProducts = true;
        } else {
          draft.events = (action as GetEventsAsyncSuccess).payload;
          draft.fetchCompleted.events = true;
        }
        break;
      case types.GET_EVENTDETAILS_ASYNC.SUCCESS:
      case types.SET_EVENT_DETAILS:
        draft.eventDetails = (action as GetEventDetailsAsyncSuccess | SetEventDetails).payload;
        draft.fetchCompleted.eventDetails = true;
        break;
      case types.GET_MEMBERSHIP_PRODUCT_ASYNC.SUCCESS:
      case types.SET_MEMBERSHIP_PRODUCT:
        draft.membershipProduct = (action as GetMembershipProductAsyncSuccess | SetMembershipProduct).payload;
        draft.fetchCompleted.membershipProduct = true;
        break;
      case types.CREATE_SESSION_ASYNC.SUCCESS:
        draft.session = (action as CreateSessionAsyncSuccess).payload;
        break;
      case types.GET_EVENTS_ASYNC.ERROR:
        draft.error = (action as GetEventsAsyncError).payload;
        draft.fetchCompleted.events = true;
        break;
      case types.GET_EVENTDETAILS_ASYNC.ERROR:
        draft.error = (action as GetEventDetailsAsyncError).payload;
        draft.fetchCompleted.eventDetails = true;
        break;
      case types.GET_MEMBERSHIP_PRODUCT_ASYNC.ERROR:
        draft.error = (action as GetMembershipProductAsyncError).payload;
        draft.fetchCompleted.membershipProduct = true;
        break;
      case types.CREATE_SESSION_ASYNC.ERROR:
      case types.RESET_EVENT_DATA_ASYNC.ERROR:
      case types.EMAIL_TICKETS_ASYNC.ERROR:
      case types.TEXT_TICKETS_ASYNC.ERROR:
      case types.VALIDATE_MEMBERSHIP_ASYNC.ERROR:
        draft.error = (
          action as
            | GetEventsAsyncError
            | GetEventDetailsAsyncError
            | ResetEventDataAsyncError
            | CreateSessionAsyncError
            | EmailTicketsAsyncError
            | TextTicketsAsyncError
            | ValidateMembershipAsyncError
        ).payload;
        break;
      case types.GET_EVENTS_ASYNC.PENDING:
      case types.GET_EVENTDETAILS_ASYNC.PENDING:
      case types.GET_MEMBERSHIP_PRODUCT_ASYNC.PENDING:
      case types.CREATE_SESSION_ASYNC.PENDING:
      case types.RESET_EVENT_DATA_ASYNC.PENDING:
      case types.EMAIL_TICKETS_ASYNC.PENDING:
      case types.TEXT_TICKETS_ASYNC.PENDING:
      case types.VALIDATE_MEMBERSHIP_ASYNC.PENDING:
        draft.loading = (
          action as
            | GetEventsAsyncPending
            | GetEventDetailsAsyncPending
            | GetMembershipProductAsyncPending
            | CreateSessionAsyncPending
            | ResetEventDataAsyncPending
            | EmailTicketsAsyncPending
            | TextTicketsAsyncPending
            | ValidateMembershipAsyncPending
        ).payload;
        break;
      case types.SET_GENRES_CONFIG:
        draft.genresConfig = (action as SetGenresConfig).payload;
        break;
      case types.SET_EVENTS_CONFIG:
        draft.eventsConfig = (action as SetEventsConfig).payload;
        break;
      case types.SET_TIERS_CONFIG:
        draft.tiersConfig = (action as SetTiersConfig).payload;
        break;
      case types.SET_MEMBERSHIP_PRODUCT_CONFIG:
        draft.membershipProductConfig = (action as SetMembershipProductConfig).payload;
        break;
      case types.SET_CONFIG_PATH:
        draft.configPath = (action as SetConfigPath).payload;
        break;
      case types.SET_NAVIGATION_PATH:
        draft.navigationPath = (action as SetNavigationPath).payload;
        break;
      case types.SET_TIMER_OVERLAY:
        draft.timerOverlay = (action as SetTimerOverlay).payload;
        break;
      case types.SET_SELECTED_PROMO_CODE:
        draft.priceTypePromoCode.selectedCode = (action as SetSelectedPromoCode).payload;
        break;
      case types.SAVE_PREFERRED_DELIVERY_METHOD:
        draft.delivery.preferredMethod = (action as SavePreferredDeliveryMethod).payload;
        break;
      case types.SAVE_DELIVERY_EMAIL:
        draft.delivery.email = (action as SaveDeliveryEmail).payload;
        break;
      case types.SAVE_DELIVERY_PHONE_NUMBER:
        draft.delivery.phone = (action as SaveDeliveryPhoneNumber).payload;
        break;
      case types.EMAIL_TICKETS_ASYNC.SUCCESS:
      case types.TEXT_TICKETS_ASYNC.SUCCESS:
      case types.SET_DELIVERY_STATUS:
        draft.delivery.status = (
          action as EmailTicketsAsyncSuccess | SetDeliveryStatus | TextTicketsAsyncSuccess
        ).payload;
        // Add a response to stop email animation
        break;
      case types.APPLY_PRICE_TYPE_PROMO_CODE_ASYNC.REQUEST:
        draft.priceTypePromoCode.processed = false;
        break;
      case types.APPLY_PRICE_TYPE_PROMO_CODE_ASYNC.SUCCESS:
        draft.eventDetails = (action as ApplyPriceTypePromoCodeAsyncSuccess).payload;
        draft.priceTypePromoCode = {
          ...initialState.priceTypePromoCode,
          processed: true
        };
        draft.appliedPriceTypePromo = (action as ApplyPriceTypePromoCodeAsyncSuccess).payload.promoCode;
        break;
      case types.APPLY_PRICE_TYPE_PROMO_CODE_ASYNC.PENDING:
        draft.priceTypePromoCode.loading = (action as ApplyPriceTypePromoCodeAsyncPending).payload;
        break;
      case types.APPLY_PRICE_TYPE_PROMO_CODE_ASYNC.ERROR:
        draft.priceTypePromoCode.error = (action as ApplyPriceTypePromoCodeAsyncError).payload;
        break;
      case types.VALIDATE_MEMBERSHIP_ASYNC.SUCCESS:
        draft.membershipDetails = {
          ...draft.membershipDetails,
          ...(action as ValidateMembershipAsyncSuccess).payload
        };
        break;
      case types.RESET_PRICE_TYPE_PROMO_CODE:
        draft.priceTypePromoCode = initialState.priceTypePromoCode;
        break;
      case types.RESET_APPLIED_PRICE_TYPE_PROMO:
        draft.appliedPriceTypePromo = initialState.appliedPriceTypePromo;
        break;
      case types.RESET_SESSION:
        draft.session = null;
        break;
      case types.RESET_TICKETING:
        // Keep the session so we can release the cart
        return {
          ...initialState,
          session: draft.session
        };
      case types.RESET_EVENTS:
        draft.events = [];
        draft.fetchCompleted.events = false;
        break;
      case types.RESET_UPSELL_PRODUCTS:
        draft.upsellProducts = [];
        draft.fetchCompleted.upsellProducts = false;
        break;
      case types.RESET_EVENT_DETAILS:
        draft.eventDetails = null;
        draft.fetchCompleted.eventDetails = false;
        break;
      case types.RESET_UPSELL:
        draft.upsell = { ...initialState.upsell };
        break;
      case types.RESET_DELIVERY:
        draft.delivery = initialState.delivery;
        break;
      case types.RESET_MEMBERSHIP_PRODUCT:
        draft.membershipProduct = null;
        draft.fetchCompleted.membershipProduct = false;
        break;
      case types.RESET_MEMBERSHIP_DETAILS:
        draft.membershipDetails = {
          ...initialState.membershipDetails
        };
        break;
      case types.RESET_ERROR:
        draft.error = undefined;
        break;
      case types.RESET_SELECTED_PROMO_CODE:
        draft.priceTypePromoCode.selectedCode = undefined;
        break;
      case types.SET_UPSELL:
        draft.upsell = (action as SetUpsell).payload;
        break;
      default:
    }
  });
};
